import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/hooks/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { 
  Users, 
  Calendar, 
  FileText, 
  CheckCircle2, 
  Clock, 
  UserPlus, 
  BookOpen, 
  Award,
  Plus,
  Edit,
  Trash2,
  GripVertical
} from 'lucide-react';
import { format, addDays, parseISO } from 'date-fns';

interface OnboardingTemplate {
  id: string;
  business_id: string;
  name: string;
  description?: string;
  duration_days: number;
  is_active: boolean;
  tasks: OnboardingTask[];
  created_at: string;
}

interface OnboardingTask {
  id: string;
  title: string;
  description?: string;
  day_offset: number; // Days from hire date
  category: 'paperwork' | 'training' | 'meeting' | 'equipment' | 'system-access' | 'other';
  priority: 'low' | 'medium' | 'high';
  estimated_hours?: number;
  assigned_to?: string; // User ID or role
  dependencies?: string[]; // Task IDs that must be completed first
  is_required: boolean;
  order: number;
}

interface OnboardingInstance {
  id: string;
  business_id: string;
  job_application_id: string;
  template_id: string;
  employee_name: string;
  start_date: string;
  status: 'pending' | 'in-progress' | 'completed' | 'paused';
  progress_percentage: number;
  tasks: OnboardingTaskInstance[];
  created_at: string;
}

interface OnboardingTaskInstance {
  id: string;
  onboarding_id: string;
  task_template_id: string;
  title: string;
  description?: string;
  due_date: string;
  category: string;
  priority: string;
  status: 'pending' | 'in-progress' | 'completed' | 'skipped';
  assigned_to?: string;
  completed_by?: string;
  completed_at?: string;
  notes?: string;
}

interface OnboardingWorkflowManagerProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  selectedApplication?: any; // Job application that was hired
}

const OnboardingWorkflowManager: React.FC<OnboardingWorkflowManagerProps> = ({
  isOpen,
  onClose,
  businessId,
  selectedApplication
}) => {
  const [templates, setTemplates] = useState<OnboardingTemplate[]>([]);
  const [onboardingInstances, setOnboardingInstances] = useState<OnboardingInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('templates');
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<OnboardingTemplate | null>(null);

  // Template form state
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    duration_days: 30,
    is_active: true
  });

  const [templateTasks, setTemplateTasks] = useState<OnboardingTask[]>([]);

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
      fetchOnboardingInstances();
    }
  }, [isOpen, businessId]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      // For now, we'll store templates as JSON in a simple table
      // In a real implementation, you'd have dedicated tables for onboarding templates and tasks
      const { data, error } = await supabase
        .from('onboarding_templates')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching templates:', error);
        // If table doesn't exist, that's okay - we'll show empty state
        setTemplates([]);
      } else {
        setTemplates(data || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchOnboardingInstances = async () => {
    try {
      // Fetch active onboarding processes
      const { data, error } = await supabase
        .from('onboarding_instances')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching onboarding instances:', error);
        setOnboardingInstances([]);
      } else {
        setOnboardingInstances(data || []);
      }
    } catch (error) {
      console.error('Error fetching onboarding instances:', error);
      setOnboardingInstances([]);
    }
  };

  const createDefaultTemplate = () => {
    const defaultTasks: OnboardingTask[] = [
      {
        id: '1',
        title: 'Complete Employment Paperwork',
        description: 'Fill out tax forms, direct deposit, emergency contacts',
        day_offset: 0,
        category: 'paperwork',
        priority: 'high',
        estimated_hours: 2,
        is_required: true,
        order: 1
      },
      {
        id: '2',
        title: 'IT Setup - Computer & Accounts',
        description: 'Set up work computer, email, and system accounts',
        day_offset: 0,
        category: 'equipment',
        priority: 'high',
        estimated_hours: 1,
        is_required: true,
        order: 2
      },
      {
        id: '3',
        title: 'Office Tour & Introduction',
        description: 'Meet the team and learn about the workspace',
        day_offset: 0,
        category: 'meeting',
        priority: 'medium',
        estimated_hours: 1,
        is_required: true,
        order: 3
      },
      {
        id: '4',
        title: 'Safety Training',
        description: 'Complete workplace safety and emergency procedures training',
        day_offset: 1,
        category: 'training',
        priority: 'high',
        estimated_hours: 2,
        is_required: true,
        order: 4
      },
      {
        id: '5',
        title: 'Job-Specific Training',
        description: 'Learn role-specific procedures and systems',
        day_offset: 2,
        category: 'training',
        priority: 'high',
        estimated_hours: 8,
        is_required: true,
        order: 5
      },
      {
        id: '6',
        title: 'First Week Check-in',
        description: 'Meeting with supervisor to discuss progress',
        day_offset: 5,
        category: 'meeting',
        priority: 'medium',
        estimated_hours: 0.5,
        is_required: true,
        order: 6
      },
      {
        id: '7',
        title: '30-Day Review',
        description: 'Formal review of performance and feedback',
        day_offset: 30,
        category: 'meeting',
        priority: 'high',
        estimated_hours: 1,
        is_required: true,
        order: 7
      }
    ];

    setTemplateForm({
      name: 'Standard Employee Onboarding',
      description: 'Complete onboarding process for new employees',
      duration_days: 30,
      is_active: true
    });
    setTemplateTasks(defaultTasks);
    setIsCreatingTemplate(true);
  };

  const addTask = () => {
    const newTask: OnboardingTask = {
      id: `task_${Date.now()}`,
      title: 'New Task',
      description: '',
      day_offset: 0,
      category: 'other',
      priority: 'medium',
      estimated_hours: 1,
      is_required: false,
      order: templateTasks.length + 1
    };
    setTemplateTasks([...templateTasks, newTask]);
  };

  const updateTask = (taskId: string, updates: Partial<OnboardingTask>) => {
    setTemplateTasks(prev => 
      prev.map(task => 
        task.id === taskId ? { ...task, ...updates } : task
      )
    );
  };

  const removeTask = (taskId: string) => {
    setTemplateTasks(prev => prev.filter(task => task.id !== taskId));
  };

  const moveTask = (taskId: string, direction: 'up' | 'down') => {
    const taskIndex = templateTasks.findIndex(t => t.id === taskId);
    if (taskIndex === -1) return;
    
    const newTasks = [...templateTasks];
    const targetIndex = direction === 'up' ? taskIndex - 1 : taskIndex + 1;
    
    if (targetIndex >= 0 && targetIndex < newTasks.length) {
      [newTasks[taskIndex], newTasks[targetIndex]] = [newTasks[targetIndex], newTasks[taskIndex]];
      
      newTasks.forEach((task, index) => {
        task.order = index + 1;
      });
      
      setTemplateTasks(newTasks);
    }
  };

  const saveTemplate = async () => {
    if (!templateForm.name.trim()) {
      toast.error('Template name is required');
      return;
    }

    try {
      const templateData = {
        business_id: businessId,
        name: templateForm.name.trim(),
        description: templateForm.description.trim() || null,
        duration_days: templateForm.duration_days,
        is_active: templateForm.is_active,
        tasks: templateTasks
      };

      if (editingTemplate) {
        const { error } = await supabase
          .from('onboarding_templates')
          .update(templateData)
          .eq('id', editingTemplate.id);

        if (error) throw error;
        toast.success('Template updated successfully');
      } else {
        const { error } = await supabase
          .from('onboarding_templates')
          .insert(templateData);

        if (error) throw error;
        toast.success('Template created successfully');
      }

      setIsCreatingTemplate(false);
      setEditingTemplate(null);
      resetTemplateForm();
      fetchTemplates();
    } catch (error: unknown) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    }
  };

  const resetTemplateForm = () => {
    setTemplateForm({
      name: '',
      description: '',
      duration_days: 30,
      is_active: true
    });
    setTemplateTasks([]);
  };

  const startOnboarding = async (templateId: string) => {
    if (!selectedApplication) {
      toast.error('No hired candidate selected');
      return;
    }

    try {
      const template = templates.find(t => t.id === templateId);
      if (!template) throw new Error('Template not found');

      const onboardingData = {
        business_id: businessId,
        job_application_id: selectedApplication.id,
        template_id: templateId,
        employee_name: `${selectedApplication.first_name} ${selectedApplication.last_name}`,
        start_date: new Date().toISOString().split('T')[0],
        status: 'pending',
        progress_percentage: 0
      };

      const { error } = await supabase
        .from('onboarding_instances')
        .insert(onboardingData);

      if (error) throw error;

      toast.success('Onboarding process started successfully');
      fetchOnboardingInstances();
    } catch (error: unknown) {
      console.error('Error starting onboarding:', error);
      toast.error('Failed to start onboarding process');
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'paperwork': return <FileText className="h-4 w-4" />;
      case 'training': return <BookOpen className="h-4 w-4" />;
      case 'meeting': return <Users className="h-4 w-4" />;
      case 'equipment': return <Award className="h-4 w-4" />;
      case 'system-access': return <UserPlus className="h-4 w-4" />;
      default: return <CheckCircle2 className="h-4 w-4" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'low': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Onboarding Workflow Manager
          </DialogTitle>
          <DialogDescription>
            Create templates and manage employee onboarding processes
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="h-[75vh]">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="templates">Templates</TabsTrigger>
            <TabsTrigger value="active">Active Onboarding</TabsTrigger>
            <TabsTrigger value="create">Create/Edit Template</TabsTrigger>
          </TabsList>

          <TabsContent value="templates" className="space-y-4 mt-4 h-full overflow-y-auto">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Onboarding Templates</h3>
              <Button onClick={createDefaultTemplate}>
                <Plus className="h-4 w-4 mr-2" />
                Create Template
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading templates...</p>
              </div>
            ) : templates.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <BookOpen className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Templates Yet</h3>
                  <p className="text-muted-foreground text-center mb-4">
                    Create your first onboarding template to streamline the new employee experience.
                  </p>
                  <Button onClick={createDefaultTemplate}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Your First Template
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map((template) => (
                  <Card key={template.id}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-sm font-medium">{template.name}</CardTitle>
                          <p className="text-sm text-muted-foreground mt-1">
                            {template.duration_days} days • {template.tasks?.length || 0} tasks
                          </p>
                        </div>
                        <Badge variant={template.is_active ? 'default' : 'secondary'}>
                          {template.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {template.description && (
                        <p className="text-sm text-muted-foreground mb-4">
                          {template.description}
                        </p>
                      )}
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingTemplate(template);
                            setTemplateForm({
                              name: template.name,
                              description: template.description || '',
                              duration_days: template.duration_days,
                              is_active: template.is_active
                            });
                            setTemplateTasks(template.tasks || []);
                            setActiveTab('create');
                          }}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        
                        {selectedApplication && (
                          <Button
                            size="sm"
                            onClick={() => startOnboarding(template.id)}
                          >
                            <UserPlus className="h-4 w-4 mr-1" />
                            Start Onboarding
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="active" className="space-y-4 mt-4 h-full overflow-y-auto">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Active Onboarding Processes</h3>
            </div>

            {onboardingInstances.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Clock className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Active Onboarding</h3>
                  <p className="text-muted-foreground text-center">
                    Once you hire candidates and start their onboarding, they'll appear here.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {onboardingInstances.map((instance) => (
                  <Card key={instance.id}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-sm font-medium">{instance.employee_name}</CardTitle>
                          <p className="text-sm text-muted-foreground">
                            Started: {format(parseISO(instance.start_date), 'PPP')}
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge variant="default">{instance.status}</Badge>
                          <p className="text-sm text-muted-foreground mt-1">
                            {instance.progress_percentage}% Complete
                          </p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                        <div 
                          className="bg-primary h-2 rounded-full transition-all duration-300"
                          style={{ width: `${instance.progress_percentage}%` }}
                        ></div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm">
                          <FileText className="h-4 w-4 mr-1" />
                          View Tasks
                        </Button>
                        <Button variant="outline" size="sm">
                          <Calendar className="h-4 w-4 mr-1" />
                          Update Progress
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="create" className="space-y-4 mt-4 h-full overflow-y-auto">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">
                {editingTemplate ? 'Edit Template' : 'Create New Template'}
              </h3>
              <Button variant="outline" onClick={() => {
                setActiveTab('templates');
                setIsCreatingTemplate(false);
                setEditingTemplate(null);
                resetTemplateForm();
              }}>
                Cancel
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Template Details */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                  <CardTitle className="text-sm font-medium">Template Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="template_name">Template Name *</Label>
                    <Input
                      id="template_name"
                      value={templateForm.name}
                      onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Standard Employee Onboarding"
                    />
                  </div>

                  <div>
                    <Label htmlFor="template_description">Description</Label>
                    <Textarea
                      id="template_description"
                      value={templateForm.description}
                      onChange={(e) => setTemplateForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Brief description of this onboarding process"
                      rows={3}
                    />
                  </div>

                  <div>
                    <Label htmlFor="duration_days">Duration (Days)</Label>
                    <Input
                      id="duration_days"
                      type="number"
                      min="1"
                      max="365"
                      value={templateForm.duration_days}
                      onChange={(e) => setTemplateForm(prev => ({ ...prev, duration_days: parseInt(e.target.value) || 30 }))}
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_active"
                      checked={templateForm.is_active}
                      onCheckedChange={(checked) => setTemplateForm(prev => ({ ...prev, is_active: checked }))}
                    />
                    <Label htmlFor="is_active">Active Template</Label>
                  </div>

                  <Button onClick={saveTemplate} className="w-full">
                    {editingTemplate ? 'Update Template' : 'Save Template'}
                  </Button>
                </CardContent>
              </Card>

              {/* Tasks */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-sm font-medium">Onboarding Tasks</CardTitle>
                    <Button size="sm" onClick={addTask}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Task
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                    {templateTasks.map((task, index) => (
                      <Card key={task.id} className="p-3">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-1">
                              <GripVertical className="h-4 w-4 text-muted-foreground cursor-move" />
                              <Input
                                value={task.title}
                                onChange={(e) => updateTask(task.id, { title: e.target.value })}
                                className="text-sm"
                                placeholder="Task title"
                              />
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => moveTask(task.id, 'up')}
                                disabled={index === 0}
                              >
                                ↑
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => moveTask(task.id, 'down')}
                                disabled={index === templateTasks.length - 1}
                              >
                                ↓
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeTask(task.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs">Day</Label>
                              <Input
                                type="number"
                                min="0"
                                value={task.day_offset}
                                onChange={(e) => updateTask(task.id, { day_offset: parseInt(e.target.value) || 0 })}
                                className="text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Hours</Label>
                              <Input
                                type="number"
                                min="0"
                                step="0.5"
                                value={task.estimated_hours || 0}
                                onChange={(e) => updateTask(task.id, { estimated_hours: parseFloat(e.target.value) || 0 })}
                                className="text-sm"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            <Select
                              value={task.category}
                              onValueChange={(value: unknown) => updateTask(task.id, { category: value })}
                            >
                              <SelectTrigger className="text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="paperwork">Paperwork</SelectItem>
                                <SelectItem value="training">Training</SelectItem>
                                <SelectItem value="meeting">Meeting</SelectItem>
                                <SelectItem value="equipment">Equipment</SelectItem>
                                <SelectItem value="system-access">System Access</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>

                            <Select
                              value={task.priority}
                              onValueChange={(value: unknown) => updateTask(task.id, { priority: value })}
                            >
                              <SelectTrigger className="text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="low">Low</SelectItem>
                                <SelectItem value="medium">Medium</SelectItem>
                                <SelectItem value="high">High</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id={`required_${task.id}`}
                              checked={task.is_required}
                              onCheckedChange={(checked) => updateTask(task.id, { is_required: !!checked })}
                            />
                            <Label htmlFor={`required_${task.id}`} className="text-sm">Required</Label>
                          </div>

                          <Textarea
                            value={task.description || ''}
                            onChange={(e) => updateTask(task.id, { description: e.target.value })}
                            placeholder="Task description..."
                            className="text-sm"
                            rows={2}
                          />
                        </div>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingWorkflowManager;